import { v4 as uuid } from 'uuid';
import * as zod from 'zod';

import { CartesianPose, ZERO_POSE } from '@sb/geometry';
import {
  IntegrationInterfaceKind,
  IntegrationControlBoxIOInterface,
  IntegrationFlangeIOInterface,
} from '@sb/integrations/io/types';
import { CollisionBoxConfig } from '@sb/integrations/types/CollisionBox';
import {
  CustomEquipmentActionConfiguration,
  CustomEquipmentSensorConfiguration,
  CustomEquipmentConfiguration,
} from '@sb/integrations/types/customEquipmentTypes';

export const CustomGripperTooltipConfiguration = zod.object({
  name: zod.string().default('Tooltip'),
  tcpTransform: CartesianPose.default(ZERO_POSE),
});

export type CustomGripperTooltipConfiguration = zod.infer<
  typeof CustomGripperTooltipConfiguration
>;

// Extend CustomEquipmentActionConfiguration for CustomGripper
export const CustomGripperActionConfiguration =
  CustomEquipmentActionConfiguration.extend({
    // Add any CustomGripper-specific action fields here if needed
  });

export type CustomGripperActionConfiguration = zod.infer<
  typeof CustomGripperActionConfiguration
>;

// Use CustomEquipmentSensorConfiguration for CustomGripper
export const CustomGripperSensorsConfiguration =
  CustomEquipmentSensorConfiguration;

export type CustomGripperSensorsConfiguration = zod.infer<
  typeof CustomGripperSensorsConfiguration
>;

// CustomGripper Configuration
export const CustomGripperConfiguration = CustomEquipmentConfiguration.extend({
  kind: zod.literal('CustomGripper'),
  name: zod.string().default('Custom gripper'),
  sensors: zod.array(CustomGripperSensorsConfiguration).default([]),
  actions: zod.array(CustomGripperActionConfiguration).default([
    CustomGripperActionConfiguration.parse({
      id: uuid(),
      name: 'Actuate',
      kind: 'actuate',
      actionOutputs: {
        kind: IntegrationInterfaceKind.Values.controlBoxIO,
        outputs: [],
        pulseSeconds: 0.5,
      },
    }),
    CustomGripperActionConfiguration.parse({
      id: uuid(),
      name: 'Reset',
      kind: 'reset',
      actionOutputs: {
        kind: IntegrationInterfaceKind.Values.controlBoxIO,
        outputs: [],
        pulseSeconds: 0.5,
      },
    }),
  ]),
  interfaces: zod
    .array(
      zod.union([
        IntegrationControlBoxIOInterface,
        IntegrationFlangeIOInterface,
      ]),
    )
    .default([]),
  /**
   * The gripper's tooltips, addressed by slot: an entry's position in this
   * array is the tooltip's permanent identity. It is the `<N>` in the
   * `ee-CustomGripper-<N>` option every stored reference holds -- the Active
   * Tooltip, Set Active Tooltip steps, a Move or waypoint's explicit tooltip,
   * a pose stamped with the tooltip it was taught at -- and the `<N>` in the
   * `<id>_tcp_<N>` link the URDF mints for it.
   *
   * Deleting a tooltip therefore empties its slot rather than closing the
   * gap, so no surviving tooltip ever inherits a name something else already
   * points at. An empty slot publishes no option and resolves to the wrist
   * flange, which is the honest answer for a tooltip that no longer exists.
   */
  tooltips: zod
    .array(CustomGripperTooltipConfiguration.nullable())
    .default([CustomGripperTooltipConfiguration.parse({})]),
  collisionBox: CollisionBoxConfig.optional(),
});

export type CustomGripperConfiguration = zod.infer<
  typeof CustomGripperConfiguration
>;
